import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';

// Lightweight, dependency-free product tour. Renders a dimmed overlay with a
// spotlight cut around a target element and an explanatory card beside it, or a
// centered card when a step has no target (welcome / finish, or a target that
// isn't on screen). Steps are plain data:
//   { target: '[data-tour="nav-settings"]' | null, title, body, primary? }
//
// Targets are matched by selector; when several elements match (e.g. the nav
// exists in both the mobile bottom bar and the desktop sidebar) the first
// VISIBLE one wins, so the same step works on phone and desktop.

function getVisibleTarget(selector) {
  if (!selector) return null;
  const els = document.querySelectorAll(selector);
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (el.offsetParent !== null && r.width > 0 && r.height > 0) return el;
  }
  return null;
}

const SPOT_PAD = 8;   // breathing room around the highlighted element
const GAP = 14;       // gap between target and card
const CARD_W = 320;   // approx card width, for placement math
const EDGE = 12;      // min viewport margin

export default function GuidedTour({ steps, run, onFinish }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const total = steps.length;
  const step = run && i < total ? steps[i] : null;

  // Restart at the first step each time a run begins.
  useEffect(() => { if (run) setI(0); }, [run]);

  const measure = useCallback(() => {
    const el = getVisibleTarget(step ? step.target : null);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  // On each step: scroll the target into view, then measure once laid out.
  useLayoutEffect(() => {
    if (!step) return undefined;
    const el = getVisibleTarget(step.target);
    if (el) { try { el.scrollIntoView({ block: 'center', inline: 'center' }); } catch { /* ignore */ } }
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [step, measure]);

  // Keep the spotlight glued to the target as things move.
  useEffect(() => {
    if (!step) return undefined;
    const on = () => measure();
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
  }, [step, measure]);

  const finish = useCallback(() => { onFinish && onFinish(); }, [onFinish]);

  // Keyboard: Esc to leave, arrows to move.
  useEffect(() => {
    if (!step) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') { if (i >= total - 1) finish(); else setI(n => n + 1); }
      else if (e.key === 'ArrowLeft') setI(n => Math.max(0, n - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, i, total, finish]);

  if (!step) return null;

  const isFirst = i === 0;
  const isLast = i === total - 1;
  const next = () => { if (isLast) finish(); else setI(n => n + 1); };
  const back = () => setI(n => Math.max(0, n - 1));

  // ── Placement ──────────────────────────────────────────────────
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let placement = 'center';
  const cardStyle = {};
  let spotStyle = null;

  if (rect) {
    spotStyle = {
      top: rect.top - SPOT_PAD,
      left: rect.left - SPOT_PAD,
      width: rect.width + SPOT_PAD * 2,
      height: rect.height + SPOT_PAD * 2,
    };
    const below = vh - (rect.top + rect.height);
    const above = rect.top;
    const right = vw - (rect.left + rect.width);
    const left = rect.left;

    if (below > 190) placement = 'bottom';
    else if (above > 190) placement = 'top';
    else if (right > 320) placement = 'right';
    else if (left > 320) placement = 'left';
    else placement = 'bottom';

    const clampX = (x) => Math.max(EDGE, Math.min(x, vw - CARD_W - EDGE));
    const centerX = rect.left + rect.width / 2 - CARD_W / 2;
    const centerY = rect.top + rect.height / 2;

    if (placement === 'bottom') {
      cardStyle.top = rect.top + rect.height + GAP;
      cardStyle.left = clampX(centerX);
    } else if (placement === 'top') {
      cardStyle.top = rect.top - GAP;
      cardStyle.left = clampX(centerX);
      cardStyle.transform = 'translateY(-100%)';
    } else if (placement === 'right') {
      cardStyle.left = rect.left + rect.width + GAP;
      cardStyle.top = Math.max(EDGE, Math.min(centerY, vh - 160));
      cardStyle.transform = 'translateY(-50%)';
    } else { // left
      cardStyle.left = rect.left - GAP;
      cardStyle.top = Math.max(EDGE, Math.min(centerY, vh - 160));
      cardStyle.transform = 'translate(-100%, -50%)';
    }
  }

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="App tour">
      {/* Click blocker — swallows taps so the tour drives the flow. When a
          target is highlighted the dim comes from the spotlight's shadow, so
          the blocker itself is transparent; centered steps dim it directly. */}
      <div className={`tour-blocker ${rect ? '' : 'tour-blocker--dim'}`} onClick={finish} />

      {rect && <div className="tour-spotlight" style={spotStyle} />}

      <div className={`tour-card tour-card--${placement}`} style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tour-card__x" onClick={finish} aria-label="Close tour">×</button>
        <div className="tour-card__step">{i + 1} of {total}</div>
        <div className="tour-card__title">{step.title}</div>
        <div className="tour-card__body">{step.body}</div>
        <div className="tour-card__actions">
          {!isFirst && <button type="button" className="tour-btn tour-btn--ghost" onClick={back}>Back</button>}
          <div className="tour-card__spacer" />
          {!isLast && <button type="button" className="tour-btn tour-btn--ghost" onClick={finish}>Skip</button>}
          <button type="button" className="tour-btn tour-btn--primary" onClick={next}>
            {isFirst ? (step.primary || 'Start') : isLast ? (step.primary || 'Done') : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
